import { useState, useEffect } from 'react'
import {
  Table,
  Tag,
  Space,
  Select,
  Button,
  Typography,
  Card,
  message,
  Drawer,
  Descriptions,
  Popconfirm,
  Modal,
  Input
} from 'antd'
import { ReloadOutlined, EyeOutlined, RedoOutlined, FilePdfOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import transactionService from '../services/transactionService'
import api from '../services/api'

const { Title } = Typography
const { Option } = Select

// Backend historically stored naive ISO timestamps (no timezone). Those get parsed as "local time"
// and appear shifted vs the email date. Normalize to UTC by appending 'Z' when timezone is missing.
const normalizeIsoTimestamp = (ts) => {
  if (!ts) return ts
  if (typeof ts !== 'string') return ts
  // If it already has timezone info (Z or ±HH:mm), keep as-is.
  if (/Z$|[+-]\d{2}:\d{2}$/.test(ts)) return ts
  return `${ts}Z`
}

const formatTimestampLocal = (ts) => {
  if (!ts) return 'N/A'
  return dayjs(normalizeIsoTimestamp(ts)).format('YYYY-MM-DD HH:mm:ss')
}

function TransactionsPage() {
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState(null)
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [clearConfirmText, setClearConfirmText] = useState('')

  useEffect(() => {
    fetchTransactions()
  }, [statusFilter])

  const fetchTransactions = async () => {
    setLoading(true)
    try {
      const params = {
        limit: 100
      }
      if (statusFilter) {
        params.status = statusFilter
      }
      const data = await transactionService.getAll(params)
      setTransactions(data)
    } catch (error) {
      message.error('Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }

  const handleViewDetails = (record) => {
    setSelectedTransaction(record)
    setDrawerVisible(true)
  }

  const handleRerunEmail = async (record) => {
    try {
      setLoading(true)
      // Prefer rerun by transactionId (works even if the email is no longer in polling queue).
      if (record?.transactionId) {
        await transactionService.rerunTransaction(record.transactionId)
      } else {
        await transactionService.rerunEmail(record.emailId)
      }
      message.success('Email reprocessing started successfully')
      // Refresh transactions after a short delay
      setTimeout(() => fetchTransactions(), 2000)
    } catch (error) {
      // If the request timed out on the client, the backend may still be processing.
      if (error?.code === 'ECONNABORTED') {
        message.info('Rerun is still processing. Please refresh the transaction log in ~30–60s.', 4)
        setTimeout(() => fetchTransactions(), 15000)
      } else if (error?.response?.status === 409) {
        message.info(error.response?.data?.error || 'This email was already processed successfully.')
      } else {
        message.error(error.response?.data?.error || 'Failed to rerun email')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClearHistory = async () => {
    try {
      setLoading(true)
      const resp = await transactionService.clearHistory()
      message.success(`Transaction history cleared (${resp?.deleted ?? 0} deleted)`)
      setClearModalOpen(false)
      setClearConfirmText('')
      await fetchTransactions()
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to clear transaction history')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Success':
        return 'green'
      case 'Failed':
        return 'red'
      case 'Skipped':
        return 'orange'
      case 'NonOrder':
        return 'blue'
      default:
        return 'default'
    }
  }

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => formatTimestampLocal(timestamp),
      sorter: (a, b) => dayjs(normalizeIsoTimestamp(a.timestamp)).valueOf() - dayjs(normalizeIsoTimestamp(b.timestamp)).valueOf(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'PO Number',
      dataIndex: 'poNumber',
      key: 'poNumber'
    },
    {
      title: 'SO Number',
      dataIndex: 'soNumber',
      key: 'soNumber'
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName'
    },
    {
      title: 'Items Processed',
      dataIndex: 'itemsProcessed',
      key: 'itemsProcessed'
    },
    {
      title: 'Dummy Items',
      dataIndex: 'itemsWithDummyMapping',
      key: 'itemsWithDummyMapping',
      render: (count) => (count > 0 ? <Tag color="warning">{count}</Tag> : '0')
    },
    {
      title: 'Total Value',
      dataIndex: 'totalValue',
      key: 'totalValue',
      render: (value) => {
        const num = parseFloat(value)
        return `$${!isNaN(num) ? num.toFixed(2) : '0.00'}`
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Space size="small">
          <Tag color={getStatusColor(status)}>{status}</Tag>
          {record?.isRerun ? <Tag color="purple">Rerun</Tag> : null}
        </Space>
      )
    },
    {
      title: 'PDF',
      dataIndex: 'pdfUrl',
      key: 'pdfUrl',
      render: (_, record) =>
        (record?.pdfS3Key || record?.pdfUrl) ? (
          <Button
            type="link"
            icon={<FilePdfOutlined />}
            size="small"
            onClick={async () => {
              try {
                // Always try backend endpoint first (works for both new rows with pdfS3Key
                // and legacy rows that only have pdfUrl).
                const resp = await api.get(`/transactions/${record.transactionId}/pdf-url`)
                const url = resp?.data?.url
                if (url) {
                  window.open(url, '_blank', 'noopener,noreferrer')
                  return
                }
                // Last-resort fallback (may be expired)
                if (record?.pdfUrl) window.open(record.pdfUrl, '_blank', 'noopener,noreferrer')
              } catch (e) {
                const backendMsg = e?.response?.data?.error
                message.error(backendMsg ? `Failed to open PDF: ${backendMsg}` : 'Failed to open PDF')
                // eslint-disable-next-line no-console
                console.error('PDF open error', e)
              }
            }}
          >
            View
          </Button>
        ) : (
          <span style={{ color: '#ccc' }}>N/A</span>
        )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            size="small"
          >
            Details
          </Button>
          {record.emailId &&
            record.status !== 'Success' &&
            record.status !== 'Skipped' &&
            record.status !== 'NonOrder' && (
            <Popconfirm
              title="Rerun this PO?"
              description="Full reprocess: the stored PDF is read again, re-extracted by AI with the current customer and item settings, then a new sales order is created."
              onConfirm={() => handleRerunEmail(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                icon={<RedoOutlined />}
                size="small"
                danger
              >
                Rerun
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <Title level={2} style={{ margin: 0, minWidth: '200px' }}>
            Transaction Logs
          </Title>
          <Space wrap>
            <Select
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by Status"
              allowClear
            >
              <Option value="Success">Success</Option>
              <Option value="Failed">Failed</Option>
              <Option value="Skipped">Skipped</Option>
              <Option value="NonOrder">NonOrder</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={fetchTransactions}>
              Refresh
            </Button>
            <Button
              danger
              type="dashed"
              onClick={() => setClearModalOpen(true)}
            >
              Clear History
            </Button>
          </Space>
        </div>

        <Card bodyStyle={{ padding: 0 }}>
          <Table
            dataSource={transactions}
            columns={columns}
            rowKey="transactionId"
            loading={loading}
            pagination={{ 
              pageSize: pageSize, 
              size: 'small',
              showSizeChanger: true,
              onShowSizeChange: (current, size) => setPageSize(size),
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </Space>

      <Drawer
        title="Transaction Details"
        placement="right"
        width={window.innerWidth < 768 ? '100%' : 600}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedTransaction && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Transaction ID">
              {selectedTransaction.transactionId}
            </Descriptions.Item>
            {selectedTransaction?.isRerun ? (
              <Descriptions.Item label="Rerun">
                <Tag color="purple">Rerun</Tag>
                {selectedTransaction?.rerunOfTransactionId ? (
                  <span style={{ marginLeft: 8, color: '#666' }}>
                    of {selectedTransaction.rerunOfTransactionId}
                  </span>
                ) : null}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label="Timestamp">
              {formatTimestampLocal(selectedTransaction.timestamp)}
            </Descriptions.Item>
            <Descriptions.Item label="Customer">
              {selectedTransaction.customerName}
            </Descriptions.Item>
            <Descriptions.Item label="PO Number">
              {selectedTransaction.poNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Email From">
              {selectedTransaction.emailFrom || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Email Subject">
              {selectedTransaction.emailSubject || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Email Date">
              {selectedTransaction.emailDate || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Email Message">
              {selectedTransaction.emailMessage
                ? (
                  <div style={{
                    backgroundColor: '#fafafa',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #f0f0f0',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {selectedTransaction.emailMessage}
                  </div>
                )
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="SO Number">
              {selectedTransaction.soStatusLink ? (
                <a href={selectedTransaction.soStatusLink} target="_blank" rel="noopener noreferrer" style={{color: '#1890ff'}}>
                  {selectedTransaction.soNumber || 'N/A'}
                </a>
              ) : (selectedTransaction.soNumber || 'N/A')}
            </Descriptions.Item>
            <Descriptions.Item label="SO ID">
              {selectedTransaction.soId || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedTransaction.status)}>
                {selectedTransaction.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Items Processed">
              {selectedTransaction.itemsProcessed}
            </Descriptions.Item>
            <Descriptions.Item label="Dummy Items">
              {selectedTransaction.itemsWithDummyMapping}
            </Descriptions.Item>
            <Descriptions.Item label="Total Value">
              ${(() => {
                const num = parseFloat(selectedTransaction.totalValue)
                return !isNaN(num) ? num.toFixed(2) : '0.00'
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Processing Time">
              {selectedTransaction.processingTimeMs}ms
            </Descriptions.Item>
            {selectedTransaction.pdfUrl && (
              <Descriptions.Item label="Purchase Order PDF">
                <Button
                  type="link"
                  icon={<FilePdfOutlined />}
                  onClick={async () => {
                    try {
                      const resp = await api.get(`/transactions/${selectedTransaction.transactionId}/pdf-url`)
                      const url = resp?.data?.url
                      if (url) {
                        window.open(url, '_blank', 'noopener,noreferrer')
                        return
                      }
                      if (selectedTransaction?.pdfUrl) window.open(selectedTransaction.pdfUrl, '_blank', 'noopener,noreferrer')
                    } catch (e) {
                      const backendMsg = e?.response?.data?.error
                      message.error(backendMsg ? `Failed to open PDF: ${backendMsg}` : 'Failed to open PDF')
                      // eslint-disable-next-line no-console
                      console.error('PDF open error', e)
                    }
                  }}
                >
                  Open PDF Document
                </Button>
              </Descriptions.Item>
            )}
            {selectedTransaction.statusDetails && (
              <Descriptions.Item label="Status Details">
                {selectedTransaction.statusDetails}
              </Descriptions.Item>
            )}
            {selectedTransaction.errorMessage && (
              <Descriptions.Item label="Error Message">
                <div style={{
                  color: 'red',
                  backgroundColor: '#fff2f0',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ffccc7',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {selectedTransaction.errorMessage}
                </div>
              </Descriptions.Item>
            )}
            {selectedTransaction.extractionVersion && (
              <Descriptions.Item label="AI Extraction Version">
                <Tag color={selectedTransaction.extractionVersion === 'v2' ? 'blue' : 'default'}>
                  {selectedTransaction.extractionVersion.toUpperCase()}
                </Tag>
              </Descriptions.Item>
            )}
            {selectedTransaction.aiConfidence !== undefined && selectedTransaction.aiConfidence !== null && (
              <Descriptions.Item label="AI Confidence">
                <Tag color={
                  selectedTransaction.aiConfidence >= 0.9 ? 'green' :
                  selectedTransaction.aiConfidence >= 0.7 ? 'blue' :
                  selectedTransaction.aiConfidence >= 0.5 ? 'orange' : 'red'
                }>
                  {(selectedTransaction.aiConfidence * 100).toFixed(0)}%
                </Tag>
              </Descriptions.Item>
            )}
            {selectedTransaction.aiReasoning && (
              <Descriptions.Item label="AI Extraction Reasoning">
                <div style={{
                  backgroundColor: '#f6f9fc',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #d9e2ef',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#2c3e50'
                }}>
                  {selectedTransaction.aiReasoning}
                </div>
              </Descriptions.Item>
            )}
            {selectedTransaction.aiWarnings && (
              <Descriptions.Item label="AI Extraction Warnings">
                <div style={{
                  backgroundColor: '#fffbf0',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ffe58f',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#d46b08'
                }}>
                  {selectedTransaction.aiWarnings}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>

      <Modal
        title="Clear Transaction History"
        open={clearModalOpen}
        onCancel={() => {
          setClearModalOpen(false)
          setClearConfirmText('')
        }}
        okText="Delete"
        okButtonProps={{ danger: true, disabled: clearConfirmText.trim().toLowerCase() !== 'delete', loading }}
        onOk={handleClearHistory}
      >
        <p>
          This will permanently delete <strong>all</strong> transaction logs.
          To confirm, type <strong>delete</strong> below.
        </p>
        <Input
          value={clearConfirmText}
          onChange={(e) => setClearConfirmText(e.target.value)}
          placeholder="Type delete to confirm"
          autoFocus
        />
      </Modal>
    </div>
  )
}

export default TransactionsPage
